import {
  ArrowDownTrayIcon,
  CheckCircleIcon,
  ChevronDownIcon,
  ClipboardDocumentIcon,
  ExclamationTriangleIcon,
  MagnifyingGlassIcon,
  PencilIcon,
  XCircleIcon,
} from '@heroicons/react/24/solid';
import React from 'react';

import { coproTypes } from '../../../../utils/copros';
import { generateEntryKey } from '../../../../utils/nameMatching';
import { showToast } from '../../../common/Notification/ToastrNotif.module';
import type { NotInRnicDecision, RnicNotInFileDecision } from '../ImportDetail.module';

interface ResidualReviewStepProps {
  notInRnicEntries: NotInRnicDecision[];
  rnicNotInFileEntries: RnicNotInFileDecision[];
  notInRnicSearch: string;
  onNotInRnicSearchChange: (value: string) => void;
  onClearNotInRnicSearch: () => void;
  filteredNotInRnicCount: number;
  totalNotInRnic: number;
  rnicNotInFileSearch: string;
  onRnicNotInFileSearchChange: (value: string) => void;
  onClearRnicNotInFileSearch: () => void;
  filteredRnicNotInFileCount: number;
  totalRnicNotInFile: number;
  onExportNotInRnic: () => void;
  onExportRnicNotInFile: () => void;
  onNotInRnicDecision: (entryKey: string, decision: 'force' | 'skip', forcedType?: number) => void;
  onBulkNotInRnicDecision: (decision: 'force' | 'skip', forcedType?: number) => void;
  onRnicDecision: (entryKey: string, decision: 'ignore' | 'integrate') => void;
  onBulkRnicDecision: (decision: 'ignore' | 'integrate') => void;
  editingEntryKey: string | null;
  editingImmatValue: string;
  onStartEditImmat: (entryKey: string, currentImmat: string) => void;
  onConfirmEdit: (entryKey: string) => void;
  onCancelEdit: () => void;
  setEditingImmatValue: (value: string) => void;
  typeDropdownEntryKey: string | null;
  onToggleTypeDropdown: (entryKey: string | null) => void;
  typeDropdownRef: React.RefObject<HTMLDivElement>;
  fileAddressMap: Map<string, string>;
  rnicAddressMap: Map<string, string>;
  bulkForceDropdownOpen: boolean;
  setBulkForceDropdownOpen: (open: boolean) => void;
  bulkForceDropdownRef: React.RefObject<HTMLDivElement>;
}

const FORCED_TYPE_OPTIONS = [
  { value: 1, label: coproTypes[1] },
  { value: 4, label: coproTypes[4] },
  { value: 2, label: coproTypes[2] },
  { value: 3, label: coproTypes[3] },
] as const;

const ResidualReviewStep: React.FC<ResidualReviewStepProps> = ({
  notInRnicEntries,
  rnicNotInFileEntries,
  notInRnicSearch,
  onNotInRnicSearchChange,
  onClearNotInRnicSearch,
  filteredNotInRnicCount,
  totalNotInRnic,
  rnicNotInFileSearch,
  onRnicNotInFileSearchChange,
  onClearRnicNotInFileSearch,
  filteredRnicNotInFileCount,
  totalRnicNotInFile,
  onExportNotInRnic,
  onExportRnicNotInFile,
  onNotInRnicDecision,
  onBulkNotInRnicDecision,
  onRnicDecision,
  onBulkRnicDecision,
  editingEntryKey,
  editingImmatValue,
  onStartEditImmat,
  onConfirmEdit,
  onCancelEdit,
  setEditingImmatValue,
  typeDropdownEntryKey,
  onToggleTypeDropdown,
  typeDropdownRef,
  fileAddressMap,
  rnicAddressMap,
  bulkForceDropdownOpen,
  setBulkForceDropdownOpen,
  bulkForceDropdownRef,
}) => {
  if (totalNotInRnic === 0 && totalRnicNotInFile === 0) {
    return (
      <div className="rounded-lg border border-dashed border-gray-300 bg-gray-50 px-4 py-6 text-center text-sm text-gray-600">
        Aucun résiduel à traiter. Vous pouvez passer à l'étape suivante.
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
      <div className="flex flex-col overflow-hidden rounded-lg border border-red-200 bg-red-50">
        <div className="flex-shrink-0 border-b border-red-200 bg-red-50 p-4">
          <div className="mb-3 flex items-center justify-between">
            <h3 className="flex items-center gap-2 text-base font-semibold text-red-800">
              <ExclamationTriangleIcon className="h-5 w-5" />
              Fichier importé correspondance non trouvée dans RNIC ({totalNotInRnic})
            </h3>
            <div className="flex gap-2">
              <button
                onClick={onExportNotInRnic}
                disabled={totalNotInRnic === 0}
                className="flex items-center gap-1 rounded border border-gray-300 bg-white px-2 py-1 text-xs font-medium text-gray-700 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                title="Exporter toutes les entrées"
              >
                <ArrowDownTrayIcon className="h-3 w-3" />
                Exporter
              </button>
              <div className="relative" ref={bulkForceDropdownRef}>
                <button
                  onClick={() => setBulkForceDropdownOpen(!bulkForceDropdownOpen)}
                  className="flex items-center gap-1 rounded bg-green-100 px-2 py-1 text-xs font-medium text-green-700 transition-colors hover:bg-green-200"
                >
                  Tout forcer
                  <ChevronDownIcon className="h-3 w-3" />
                </button>
                {bulkForceDropdownOpen && (
                  <div className="absolute right-0 z-50 mt-1 w-40 rounded-lg border border-gray-200 bg-white shadow-lg">
                    <div className="py-1">
                      <p className="px-3 py-1 text-[9px] font-medium text-gray-500 uppercase">Forcer comme...</p>
                      {FORCED_TYPE_OPTIONS.map((option) => (
                        <button
                          key={option.value}
                          onClick={() => {
                            onBulkNotInRnicDecision('force', option.value);
                            setBulkForceDropdownOpen(false);
                          }}
                          className="w-full px-3 py-1.5 text-left text-xs text-gray-700 transition-colors hover:bg-gray-100"
                        >
                          {option.label}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              <button
                onClick={() => onBulkNotInRnicDecision('skip')}
                className="rounded bg-red-100 px-2 py-1 text-xs font-medium text-red-700 transition-colors hover:bg-red-200"
              >
                Tout ignorer
              </button>
            </div>
          </div>
          <p className="mb-3 text-xs text-red-700">
            Copropriétés dans le fichier mais pas dans le RNIC. Corrigez l'immatriculation, forcez ou ignorez.
          </p>
          <div className="relative">
            <MagnifyingGlassIcon className="absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={notInRnicSearch}
              onChange={(e) => onNotInRnicSearchChange(e.target.value)}
              placeholder="Rechercher..."
              className="w-full rounded-lg border border-gray-300 py-1.5 pr-4 pl-9 text-sm focus:border-red-400 focus:ring-2 focus:ring-red-300 focus:outline-none"
            />
            {notInRnicSearch && (
              <button
                onClick={onClearNotInRnicSearch}
                className="absolute top-1/2 right-3 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <XCircleIcon className="h-4 w-4" />
              </button>
            )}
          </div>
          {notInRnicSearch && (
            <p className="mt-1 text-xs text-gray-500">
              {filteredNotInRnicCount} résultat(s) sur {totalNotInRnic}
            </p>
          )}
        </div>

        <div className="flex-1 overflow-y-auto">
          {notInRnicEntries.length > 0 ? (
            <table className="min-w-full divide-y divide-red-200">
              <thead className="sticky top-0 bg-red-100">
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-medium text-red-800 uppercase">Immat.</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-red-800 uppercase">Nom</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-red-800 uppercase">Adresse fichier</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-red-800 uppercase">Raison</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-red-800 uppercase">Type</th>
                  <th className="px-3 py-2 text-right text-xs font-medium text-red-800 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white">
                {notInRnicEntries.map((entry) => {
                  const entryKey = generateEntryKey(entry.immatriculation, entry.name);
                  const isEditing = editingEntryKey === entryKey;
                  const displayImmat = entry.correctedImmatriculation || entry.immatriculation;
                  const address = fileAddressMap.get(entryKey) || '';

                  return (
                    <tr key={entryKey}>
                      <td className="px-3 py-2 font-mono text-xs text-gray-600">
                        {isEditing ? (
                          <div className="flex items-center gap-1">
                            <input
                              type="text"
                              value={editingImmatValue}
                              onChange={(e) => setEditingImmatValue(e.target.value)}
                              className="w-24 rounded border border-gray-300 px-1 py-0.5 text-xs focus:ring-2 focus:ring-rgpd-blue-dark focus:outline-none"
                              autoFocus
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                  onConfirmEdit(entryKey);
                                } else if (e.key === 'Escape') {
                                  onCancelEdit();
                                }
                              }}
                            />
                            <button
                              onClick={() => onConfirmEdit(entryKey)}
                              className="p-0.5 text-green-600 hover:text-green-800"
                              title="Confirmer"
                            >
                              <CheckCircleIcon className="h-4 w-4" />
                            </button>
                            <button
                              onClick={onCancelEdit}
                              className="p-0.5 text-red-600 hover:text-red-800"
                              title="Annuler"
                            >
                              <XCircleIcon className="h-4 w-4" />
                            </button>
                          </div>
                        ) : (
                          <div className="flex items-center gap-1">
                            <span className={entry.correctedImmatriculation ? 'text-[10px] text-gray-400 line-through' : ''}>
                              {entry.immatriculation || '-'}
                            </span>
                            {entry.correctedImmatriculation && (
                              <span className="text-[10px] font-medium text-green-600">
                                → {entry.correctedImmatriculation}
                              </span>
                            )}
                            <button
                              onClick={() => onStartEditImmat(entryKey, displayImmat)}
                              className="p-0.5 text-gray-400 hover:text-rgpd-blue-dark"
                              title="Corriger l'immatriculation"
                            >
                              <PencilIcon className="h-3 w-3" />
                            </button>
                          </div>
                        )}
                      </td>
                      <td className="max-w-[150px] truncate px-3 py-2 text-xs text-gray-800" title={entry.name}>
                        {entry.name}
                      </td>
                      <td className="max-w-[180px] truncate px-3 py-2 text-xs text-gray-600" title={address || '-'}>
                        {address || <span className="text-gray-400">-</span>}
                      </td>
                      <td className="max-w-[100px] truncate px-3 py-2 text-xs text-gray-600" title={entry.reason || '-'}>
                        {entry.reason || '-'}
                      </td>
                      <td className="px-3 py-2 text-xs">
                        {entry.decision === 'force' && entry.forcedType ? (
                          <span className="rounded bg-blue-100 px-1.5 py-0.5 text-[9px] font-medium text-blue-700">
                            {coproTypes[entry.forcedType as keyof typeof coproTypes]}
                          </span>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-right">
                        <div className="flex items-center justify-end gap-1">
                          <div className="relative" ref={typeDropdownEntryKey === entryKey ? typeDropdownRef : undefined}>
                            <button
                              onClick={() => onToggleTypeDropdown(typeDropdownEntryKey === entryKey ? null : entryKey)}
                              className={`flex items-center gap-0.5 rounded px-1.5 py-0.5 text-[10px] font-medium transition-colors ${
                                entry.decision === 'force'
                                  ? 'bg-green-600 text-white'
                                  : 'bg-green-100 text-green-700 hover:bg-green-200'
                              }`}
                            >
                              Forcer
                              <ChevronDownIcon className="h-3 w-3" />
                            </button>
                            {typeDropdownEntryKey === entryKey && (
                              <div className="absolute right-0 z-50 mt-1 w-36 rounded-lg border border-gray-200 bg-white shadow-lg">
                                <div className="py-1">
                                  <p className="px-3 py-1 text-[9px] font-medium text-gray-500 uppercase">Type</p>
                                  {FORCED_TYPE_OPTIONS.map((option) => (
                                    <button
                                      key={option.value}
                                      onClick={() => onNotInRnicDecision(entryKey, 'force', option.value)}
                                      className={`w-full px-3 py-1.5 text-left text-xs transition-colors hover:bg-gray-100 ${
                                        entry.forcedType === option.value ? 'bg-green-50 font-medium text-green-700' : 'text-gray-700'
                                      }`}
                                    >
                                      {option.label}
                                    </button>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>

                          <button
                            onClick={() => onNotInRnicDecision(entryKey, 'skip')}
                            className={`rounded px-1.5 py-0.5 text-[10px] font-medium transition-colors ${
                              entry.decision === 'skip'
                                ? 'bg-red-600 text-white'
                                : 'bg-red-100 text-red-700 hover:bg-red-200'
                            }`}
                          >
                            Ignorer
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          ) : (
            <div className="flex h-full items-center justify-center px-4 py-6 text-center text-sm text-gray-500">
              Aucune entrée du fichier sans correspondance RNIC à traiter.
            </div>
          )}
        </div>
      </div>

      <div className="flex flex-col overflow-hidden rounded-lg border border-yellow-200 bg-yellow-50">
        <div className="flex-shrink-0 border-b border-yellow-200 bg-yellow-50 p-4">
          <div className="mb-3 flex items-center justify-between">
            <h3 className="flex items-center gap-2 text-base font-semibold text-yellow-800">
              <ExclamationTriangleIcon className="h-5 w-5" />
              Présent dans RNIC mais absent du fichier importé ({totalRnicNotInFile})
            </h3>
            <div className="flex gap-2">
              <button
                onClick={onExportRnicNotInFile}
                disabled={totalRnicNotInFile === 0}
                className="flex items-center gap-1 rounded border border-gray-300 bg-white px-2 py-1 text-xs font-medium text-gray-700 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                title="Exporter toutes les entrées"
              >
                <ArrowDownTrayIcon className="h-3 w-3" />
                Exporter
              </button>
              <button
                onClick={() => onBulkRnicDecision('integrate')}
                className="rounded bg-green-100 px-2 py-1 text-xs font-medium text-green-700 transition-colors hover:bg-green-200"
              >
                Tout intégrer
              </button>
              <button
                onClick={() => onBulkRnicDecision('ignore')}
                className="rounded bg-yellow-100 px-2 py-1 text-xs font-medium text-yellow-700 transition-colors hover:bg-yellow-200"
              >
                Tout ignorer
              </button>
            </div>
          </div>
          <p className="mb-3 text-xs text-yellow-700">
            Copropriétés dans le RNIC mais pas dans le fichier importé. Intégrez ou ignorez.
          </p>
          <div className="relative">
            <MagnifyingGlassIcon className="absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={rnicNotInFileSearch}
              onChange={(e) => onRnicNotInFileSearchChange(e.target.value)}
              placeholder="Rechercher..."
              className="w-full rounded-lg border border-gray-300 py-1.5 pr-4 pl-9 text-sm focus:border-yellow-400 focus:ring-2 focus:ring-yellow-300 focus:outline-none"
            />
            {rnicNotInFileSearch && (
              <button
                onClick={onClearRnicNotInFileSearch}
                className="absolute top-1/2 right-3 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <XCircleIcon className="h-4 w-4" />
              </button>
            )}
          </div>
          {rnicNotInFileSearch && (
            <p className="mt-1 text-xs text-gray-500">
              {filteredRnicNotInFileCount} résultat(s) sur {totalRnicNotInFile}
            </p>
          )}
        </div>

        <div className="flex-1 overflow-y-auto">
          {rnicNotInFileEntries.length > 0 ? (
            <table className="min-w-full divide-y divide-yellow-200">
              <thead className="sticky top-0 bg-yellow-100">
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-medium text-yellow-800 uppercase">Immat.</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-yellow-800 uppercase">Nom</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-yellow-800 uppercase">Adresse RNIC</th>
                  <th className="px-3 py-2 text-right text-xs font-medium text-yellow-800 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white">
                {rnicNotInFileEntries.map((entry) => {
                  const entryKey = generateEntryKey(entry.immatriculation, entry.name);
                  const address = rnicAddressMap.get(entryKey) || '';
                  return (
                    <tr key={entryKey}>
                      <td className="px-3 py-2 font-mono text-xs text-gray-600">
                        <div className="flex items-center gap-1">
                          <span>{entry.immatriculation || '-'}</span>
                          {entry.immatriculation && (
                            <button
                              onClick={() => {
                                navigator.clipboard.writeText(entry.immatriculation);
                                showToast('success', 'Immatriculation copiée', 2000);
                              }}
                              className="p-0.5 text-gray-400 transition-colors hover:text-yellow-600"
                              title="Copier l'immatriculation"
                            >
                              <ClipboardDocumentIcon className="h-3 w-3" />
                            </button>
                          )}
                        </div>
                      </td>
                      <td className="max-w-[200px] truncate px-3 py-2 text-xs text-gray-800" title={entry.name}>
                        {entry.name}
                      </td>
                      <td className="max-w-[200px] truncate px-3 py-2 text-xs text-gray-600" title={address || '-'}>
                        {address || <span className="text-gray-400">-</span>}
                      </td>
                      <td className="px-3 py-2 text-right">
                        <div className="flex justify-end gap-1">
                          <button
                            onClick={() => onRnicDecision(entryKey, 'integrate')}
                            className={`rounded px-1.5 py-0.5 text-[10px] font-medium transition-colors ${
                              entry.decision === 'integrate'
                                ? 'bg-green-600 text-white'
                                : 'bg-green-100 text-green-700 hover:bg-green-200'
                            }`}
                          >
                            Intégrer
                          </button>
                          <button
                            onClick={() => onRnicDecision(entryKey, 'ignore')}
                            className={`rounded px-1.5 py-0.5 text-[10px] font-medium transition-colors ${
                              entry.decision === 'ignore'
                                ? 'bg-yellow-600 text-white'
                                : 'bg-yellow-100 text-yellow-700 hover:bg-yellow-200'
                            }`}
                          >
                            Ignorer
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          ) : (
            <div className="flex h-full items-center justify-center px-4 py-6 text-center text-sm text-gray-500">
              Aucune copropriété RNIC absente du fichier à traiter.
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ResidualReviewStep;
